import Button from '@rezdy/components/Button';
import { H3 } from '@rezdy/components/Heading';
import Layout from '@rezdy/components/Layout/DarkCentered';
import TextStyle from '@rezdy/components/TextStyle';
import qs from 'query-string';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import routes from '../../constants/routes';
import auth from '../../services/auth';
import pendo from '../../services/pendo';
import routing, { push, reload } from '../../services/routing';
import { ErrorMessage } from '../ErrorMessage';
import SecondaryAction from '../Layout/SecondaryAction';

const HS_FORM_EVENT_ON_READY = 'hs-form-event:on-ready';
const HS_FORM_EVENT_ON_SUBMISSION_SUCCESS = 'hs-form-event:on-submission:success';
const HS_FORM_FIELD_FIRSTNAME = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_FIRSTNAME!;
const HS_FORM_FIELD_LASTNAME = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_LASTNAME!;
const HS_FORM_FIELD_EMAIL = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_EMAIL!;
const HS_FORM_FIELD_PHONE = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_PHONE!;
const HS_FORM_FIELD_COUNTRY = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_COUNTRY!;
const HS_FORM_FIELD_COMPANY_NAME = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_COMPANY_NAME!;
const HS_FORM_FIELD_WEBSITE = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_WEBSITE!;
const HS_FORM_FIELD_OPERATORORRESELLER = process.env.REACT_APP_HS_SIGNUP_FORM_FIELD_OPERATORORRESELLER!;
const HS_REQUIRED_FIELDS = [
    HS_FORM_FIELD_FIRSTNAME,
    HS_FORM_FIELD_LASTNAME,
    HS_FORM_FIELD_EMAIL,
    HS_FORM_FIELD_PHONE,
    HS_FORM_FIELD_COUNTRY,
    HS_FORM_FIELD_COMPANY_NAME,
    HS_FORM_FIELD_WEBSITE,
    HS_FORM_FIELD_OPERATORORRESELLER,
];
const HubspotFormDiv = styled.div`
    .hsfc-FormWrapper {
        --hsf-background__border-radius: 8px;
        --hsf-background-banner__border-bottom-right-radius: 8px;
        --hsf-background-banner__border-bottom-left-radius: 8px;
        --hsf-background-banner__border-top-right-radius: 8px;
        --hsf-background-banner__border-top-left-radius: 8px;
    }
`;
const AccountCreationErrorWrapper = styled.div`
    border-radius: 8px;
    padding: 36px;
    background: #fff;
`;
export const HubspotSignUp = ({ formId }: { formId?: string }) => {
    const [validationErrors, setValidationErrors] = useState<string>();
    const [accountCreationErrorCode, setAccountCreationErrorCode] = useState<string>();

    const getFormSubmitButtonEl = () => document.querySelector<HTMLButtonElement>("button[type='submit']")!;
    const getFormEl = () => document.querySelector<HTMLFormElement>('form.hsfc-Form')!;

    const checkAndExtractSignUpFields = async () => {
        // @ts-ignore
        const { HubSpotFormsV4 } = window;
        const fieldValues: { name: string; value: string }[] = await HubSpotFormsV4.getForms()[0].getFormFieldValues();
        const fieldValueMap: { [field: string]: string } = fieldValues
            .map(field => ({ [field.name]: field.value }))
            .reduce((keyVal1, keyVal2) => ({ ...keyVal1, ...keyVal2 }));
        if (HS_REQUIRED_FIELDS.filter(reqField => !fieldValueMap[reqField]).length > 0) {
            throw new Error(`Please fill in all required fields *`);
        }
        return {
            firstName: fieldValueMap[HS_FORM_FIELD_FIRSTNAME],
            lastName: fieldValueMap[HS_FORM_FIELD_LASTNAME],
            email: fieldValueMap[HS_FORM_FIELD_EMAIL],
            companyPhone: fieldValueMap[HS_FORM_FIELD_PHONE],
            country: fieldValueMap[HS_FORM_FIELD_COUNTRY],
            companyName: fieldValueMap[HS_FORM_FIELD_COMPANY_NAME],
            companyWebsite: fieldValueMap[HS_FORM_FIELD_WEBSITE],
            companyType: fieldValueMap[HS_FORM_FIELD_OPERATORORRESELLER],
        };
    };

    const onFormReady = () => {
        // Intercept the form submit callback so we can validate filled in data before we ship the submission to hubspot
        getFormEl().onsubmit = async (submitEvent: SubmitEvent) => {
            // Stop the form from auto submitting as we need to validate user input first
            submitEvent.preventDefault();
            submitEvent.stopPropagation();
            try {
                setValidationErrors(undefined);
                // Show user feedback on what's happening
                getFormSubmitButtonEl().innerHTML = 'Validating...';
                getFormSubmitButtonEl().setAttribute('disabled', 'true');
                await auth.hubspotSignUpValidation(await checkAndExtractSignUpFields());
                // Continue on with the hubspot form submission
                getFormEl().submit();
            } catch (err) {
                setValidationErrors(err.message);
                getFormSubmitButtonEl().innerHTML = 'Submit';
                getFormSubmitButtonEl().removeAttribute('disabled');
            }
            pendo.trackEvent(pendo.buildAnonymousVisitor(), 'Hubspot Sign Up - Form Attempted');
        };
        pendo.trackEvent(pendo.buildAnonymousVisitor(), 'Hubspot Sign Up - Form Viewed');
    };

    // Triggers after the form successfully submits. Here we will provision the new rezdy accounts
    const onFormSubmissionSuccess = async () => {
        const queryParams = qs.parse(routing.location.search);
        try {
            const signupFields = await checkAndExtractSignUpFields();
            await auth.hubspotSignUp({
                ...signupFields,
                utmSource: queryParams.utm_source as string,
                utmCampaign: queryParams.utm_campaign as string,
                utmMedium: queryParams.utm_medium as string,
                utmContent: queryParams.utm_content as string,
                utmTerm: queryParams.utm_term as string,
            });
            pendo.trackEvent(pendo.buildAnonymousVisitor(), 'Hubspot Sign Up - Accounts Created');
            push(routes.HUBSPOT_SIGN_UP_TEMP_PASSWORD, { username: signupFields.email });
        } catch (err) {
            setAccountCreationErrorCode(err.message);
        }
    };

    // On component mount, load the hubspot form scripts and register our custom callbacks
    useEffect(() => {
        const hsScript = document.createElement('script');
        hsScript.src = `https://js.hsforms.net/forms/embed/developer/${process.env.REACT_APP_HS_PORTAL_ID}.js`;
        document.head.appendChild(hsScript);

        window.addEventListener(HS_FORM_EVENT_ON_READY, onFormReady);
        window.addEventListener(HS_FORM_EVENT_ON_SUBMISSION_SUCCESS, onFormSubmissionSuccess);

        return () => {
            hsScript.remove();
            window.removeEventListener(HS_FORM_EVENT_ON_READY, onFormReady);
            window.removeEventListener(HS_FORM_EVENT_ON_SUBMISSION_SUCCESS, onFormSubmissionSuccess);
        };
    }, []);

    if (accountCreationErrorCode) {
        return (
            <AccountCreationErrorWrapper>
                <H3>Sorry, your account could not be created.</H3>
                <TextStyle as="p">We encountered the following error while creating your account:</TextStyle>
                <ErrorMessage asPlainText align="center" isDirty i18nKey={'hello this is an error'} />
                <Button type="submit" action="primary" width="100%" onClick={reload}>
                    Restart Form
                </Button>
            </AccountCreationErrorWrapper>
        );
    }
    return (
        <Layout secondaryAction={<SecondaryAction />}>
            {validationErrors && <ErrorMessage as="p" align="center" isDirty i18nKey={validationErrors} />}
            <HubspotFormDiv
                className="hs-form-html"
                data-region={process.env.REACT_APP_HS_REGION!}
                data-form-id={formId ?? process.env.REACT_APP_HS_SIGNUP_FORM_ID!}
                data-portal-id={process.env.REACT_APP_HS_PORTAL_ID!}
            />
        </Layout>
    );
};
